'use client'

import React, { useState, useEffect, useMemo } from 'react'
import type { PricingTableBlock as PricingTableBlockProps } from '@/payload-types'
import { cn } from '@/utilities/ui'
import { getBlockPadding } from '@/utilities/getBlockPadding'
import {
  ScrollableTable,
  HeaderCommitmentDropdown,
  PricingToggle,
  usePricingToggle,
  TextCell,
  PriceCell,
  CommitmentPriceCell,
  StatusPill,
  BlankCell,
  FLIP_ANIMATION_DURATION,
  getCellBackgroundStyles,
} from '@/components/tables'
import RichText from '@/components/RichText'
import type { CommitmentTerm, CellBackgroundColor, StatusIcon } from '@/components/tables'

type Props = PricingTableBlockProps & {
  disableInnerContainer?: boolean
}

interface CommitmentTermPrices {
  hourlyPrice?: string | null
  monthlyPrice?: string | null
}

interface PricingCell {
  variant?: 'text' | 'price' | 'commitment' | 'status' | 'blank' | null
  backgroundColor?: CellBackgroundColor | null
  primaryText?: string | null
  secondaryText?: string | null
  link?: string | null
  hourlyPrice?: string | null
  monthlyPrice?: string | null
  commitment6?: CommitmentTermPrices | null
  commitment12?: CommitmentTermPrices | null
  commitment24?: CommitmentTermPrices | null
  commitment36?: CommitmentTermPrices | null
  statusText?: string | null
  statusIcon?: StatusIcon | null
  id?: string | null
}

interface PricingRow {
  rowVariant?: 'cells' | 'sectionHeader' | null
  sectionHeaderText?: string | null
  sectionHeaderBackgroundColor?: CellBackgroundColor | null
  cells?: PricingCell[] | null
  id?: string | null
}

export const PricingTableBlock: React.FC<Props> = ({
  caption,
  headerRow,
  rows,
  tableNote,
  tableNoteAlignment = 'left',
  paddingTop,
  paddingBottom,
}) => {
  const paddingClasses = getBlockPadding({
    paddingTop: paddingTop ?? true,
    paddingBottom: paddingBottom ?? true,
  })

  const { pricingPeriod } = usePricingToggle()
  const [selectedCommitmentTerm, setSelectedCommitmentTerm] = useState<CommitmentTerm>('36')

  // Flip animation state for pricing toggle
  const [isPriceFlipping, setIsPriceFlipping] = useState(false)
  const [displayPeriod, setDisplayPeriod] = useState(pricingPeriod)

  // Flip animation state for commitment dropdown
  const [isCommitmentFlipping, setIsCommitmentFlipping] = useState(false)
  const [displayCommitmentTerm, setDisplayCommitmentTerm] = useState<CommitmentTerm>('36')

  const allRows = useMemo(() => (rows as PricingRow[]) || [], [rows])
  const headers = useMemo(() => headerRow || [], [headerRow])

  // Calculate number of columns for equal width sizing.
  // Fall back to the first row that has cells, so a leading section-header row
  // (which has no cells) doesn't collapse columnCount to 0.
  const columnCount =
    headers.length ||
    (allRows.find((r) => (r.cells?.length ?? 0) > 0)?.cells?.length ?? 0)

  // Auto-detect which controls to show based on cell types used
  const { hasPriceCells, hasCommitmentCells } = useMemo(() => {
    let hasPriceCells = false
    let hasCommitmentCells = false

    for (const row of allRows) {
      for (const cell of row.cells || []) {
        if (cell.variant === 'price') hasPriceCells = true
        if (cell.variant === 'commitment') hasCommitmentCells = true
        if (hasPriceCells && hasCommitmentCells) break
      }
      if (hasPriceCells && hasCommitmentCells) break
    }

    return { hasPriceCells, hasCommitmentCells }
  }, [allRows])

  // Detect which columns have commitment cells (for header dropdown placement)
  const columnsWithCommitmentCells = useMemo(() => {
    const map = new Array(columnCount).fill(false)
    for (const row of allRows) {
      for (let i = 0; i < (row.cells?.length || 0); i++) {
        if (row.cells?.[i]?.variant === 'commitment') {
          map[i] = true
        }
      }
    }
    return map
  }, [allRows, columnCount])

  // Handle the train board flip animation when pricing period changes
  useEffect(() => {
    if (pricingPeriod !== displayPeriod) {
      setIsPriceFlipping(true)
      const timer = setTimeout(() => {
        setDisplayPeriod(pricingPeriod)
        setTimeout(() => {
          setIsPriceFlipping(false)
        }, FLIP_ANIMATION_DURATION)
      }, FLIP_ANIMATION_DURATION)
      return () => clearTimeout(timer)
    }
  }, [pricingPeriod, displayPeriod])

  // Handle the train board flip animation when commitment term changes
  useEffect(() => {
    if (selectedCommitmentTerm !== displayCommitmentTerm) {
      setIsCommitmentFlipping(true)
      const timer = setTimeout(() => {
        setDisplayCommitmentTerm(selectedCommitmentTerm)
        setTimeout(() => {
          setIsCommitmentFlipping(false)
        }, FLIP_ANIMATION_DURATION)
      }, FLIP_ANIMATION_DURATION)
      return () => clearTimeout(timer)
    }
  }, [selectedCommitmentTerm, displayCommitmentTerm])

  if (allRows.length === 0) {
    return null
  }

  // Show controls based on which cell types exist in the table
  const showPricingToggle = hasPriceCells || hasCommitmentCells

  const renderCell = (cell: PricingCell) => {
    const variant = cell.variant || 'text'
    const backgroundColor = cell.backgroundColor as CellBackgroundColor | undefined

    switch (variant) {
      case 'blank':
        return <BlankCell />
      case 'status':
        return <StatusPill text={cell.statusText || ''} icon={cell.statusIcon} />
      case 'price':
        return (
          <PriceCell
            hourlyPrice={cell.hourlyPrice}
            monthlyPrice={cell.monthlyPrice}
            displayPeriod={displayPeriod}
            backgroundColor={backgroundColor}
            isFlipping={isPriceFlipping}
          />
        )
      case 'commitment':
        return (
          <CommitmentPriceCell
            commitment6={cell.commitment6}
            commitment12={cell.commitment12}
            commitment24={cell.commitment24}
            commitment36={cell.commitment36}
            displayCommitmentTerm={displayCommitmentTerm}
            displayPeriod={displayPeriod}
            backgroundColor={backgroundColor}
            isFlipping={isCommitmentFlipping || isPriceFlipping}
          />
        )
      case 'text':
      default:
        return (
          <TextCell
            primary={cell.primaryText || ''}
            secondary={cell.secondaryText}
            link={cell.link}
            backgroundColor={backgroundColor}
          />
        )
    }
  }

  return (
    <div
      className={cn(
        'box-border flex flex-col items-start justify-start relative w-full',
        paddingClasses,
        showPricingToggle && 'gap-8',
      )}
    >
      {/* Controls section - pricing toggle only (commitment dropdown is inline in header) */}
      {showPricingToggle && (
        <div className="w-full flex justify-end">
          <PricingToggle />
        </div>
      )}

      {/* Table */}
      <div className="w-full">
        <ScrollableTable>
          <table
            className="w-full border-separate border-spacing-0 table-fixed"
            style={{ minWidth: `${columnCount * 160}px` }}
          >
            {caption && <caption className="sr-only">{caption}</caption>}

            {/* Header row */}
            {headers.length > 0 && (
              <thead>
                <tr>
                  {headers.map((header, cellIndex) => {
                    const isLastColumn = cellIndex === headers.length - 1
                    const showDropdownInHeader = columnsWithCommitmentCells[cellIndex]

                    return (
                      <th
                        key={header.id || cellIndex}
                        scope="col"
                        className={cn(
                          'text-left align-top bg-civo-black',
                          'px-3 py-6',
                          !isLastColumn && 'border-r border-r-white/10',
                        )}
                      >
                        {showDropdownInHeader ? (
                          <HeaderCommitmentDropdown
                            value={selectedCommitmentTerm}
                            onValueChange={setSelectedCommitmentTerm}
                          />
                        ) : (
                          <span className="table-header text-white">{header.headerText}</span>
                        )}
                      </th>
                    )
                  })}
                </tr>
              </thead>
            )}

            {/* Body rows */}
            <tbody>
              {allRows.map((row, rowIndex) => {
                const rowVariant = row.rowVariant ?? 'cells'

                if (rowVariant === 'sectionHeader') {
                  const { bgClass } = getCellBackgroundStyles(
                    row.sectionHeaderBackgroundColor as CellBackgroundColor | undefined,
                  )

                  return (
                    <tr key={row.id || rowIndex}>
                      <td
                        colSpan={columnCount || 1}
                        className={cn('text-center align-middle', 'px-3 py-6', bgClass, 'border-b border-black')}
                      >
                        <TextCell
                          primary={row.sectionHeaderText || ''}
                          backgroundColor={row.sectionHeaderBackgroundColor}
                          align="center"
                        />
                      </td>
                    </tr>
                  )
                }

                const cells = row.cells || []

                return (
                  <tr
                    key={row.id || rowIndex}
                    className="group transition-colors duration-200 hover:bg-black/5"
                  >
                    {cells.map((cell, cellIndex) => {
                      const isLastColumn = cellIndex === cells.length - 1
                      const { bgClass } = getCellBackgroundStyles(
                        cell.backgroundColor as CellBackgroundColor | undefined,
                      )

                      return (
                        <td
                          key={cell.id || cellIndex}
                          className={cn(
                            'text-left align-top',
                            'px-3 py-6',
                            bgClass,
                            'border-b border-black',
                            !isLastColumn && 'border-r border-r-black',
                          )}
                        >
                          {renderCell(cell)}
                        </td>
                      )
                    })}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </ScrollableTable>
      </div>

      {/* Table Note */}
      {tableNote && (
        <div
          className={cn(
            'mt-6 w-full body-copy-small text-black/70 [&_strong]:text-black [&_b]:text-black',
            tableNoteAlignment === 'center' && 'text-center',
            tableNoteAlignment === 'right' && 'text-right',
          )}
        >
          <RichText data={tableNote} enableProse={false} enableGutter={false} />
        </div>
      )}
    </div>
  )
}
